import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { number } from 'prop-types';
import React, { useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import config from '../../../config';
import PriceFilterForm from '../../../forms/PriceFilterForm/PriceFilterForm';
import { formatCurrencyMajorUnit } from '../../../util/currency';
import { FormattedMessage } from '../../../util/reactIntl';

const RADIX = 10;

const getPriceQueryParamName = (queryParamNames) => {
  return Array.isArray(queryParamNames)
    ? queryParamNames[0]
    : typeof queryParamNames === 'string'
    ? queryParamNames
    : 'price';
};

// Parse value, which should look like "0,1000"
const parse = (priceRange) => {
  const [minPrice, maxPrice] = priceRange
    ? priceRange.split(',').map((v) => Number.parseInt(v, RADIX))
    : [];
  // Note: we compare to null, because 0 as minPrice is falsy in comparisons.
  return !!priceRange && minPrice != null && maxPrice != null ? { minPrice, maxPrice } : null;
};

// Format value, which should look like { minPrice, maxPrice }
const format = (range, queryParamName) => {
  const { minPrice, maxPrice } = range || {};
  // Note: we compare to null, because 0 as minPrice is falsy in comparisons.
  const value = minPrice != null && maxPrice != null ? `${minPrice},${maxPrice}` : null;
  return { [queryParamName]: value };
};

/**
 * @param {Object} props
 * @param {string} [props.id]
 * @param {string} [props.label]
 * @param {string|string[]} props.queryParamNames
 * @param {Object|null} [props.initialValues]
 * @param {number} props.min
 * @param {number} props.max
 * @param {number} [props.step]
 * @param {Function} props.onSubmit
 * @param {Object} [props.currencyConfig]
 */
const PriceFilterPlain = ({
  id,
  label,
  queryParamNames,
  initialValues = null,
  min,
  max,
  step = number,
  onSubmit,
  currencyConfig = config.currencyConfig,
}) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(true);
  const filterContentRef = useRef(null);

  const handleChange = (values) => {
    const priceQueryParamName = getPriceQueryParamName(queryParamNames);
    onSubmit(format(values, priceQueryParamName));
  };

  const handleClear = () => {
    const priceQueryParamName = getPriceQueryParamName(queryParamNames);
    onSubmit(format(null, priceQueryParamName));
  };

  const priceQueryParam = getPriceQueryParamName(queryParamNames);
  const initialPrice = initialValues ? parse(initialValues[priceQueryParam]) : {};
  const { minPrice, maxPrice } = initialPrice || {};

  const hasValue = (value) => value != null;
  const hasInitialValues = initialValues && hasValue(minPrice) && hasValue(maxPrice);

  const labelText = hasInitialValues
    ? intl.formatMessage(
        { id: 'PriceFilter.labelSelectedPlain' },
        {
          minPrice: formatCurrencyMajorUnit(intl, currencyConfig.currency, minPrice),
          maxPrice: formatCurrencyMajorUnit(intl, currencyConfig.currency, maxPrice),
        }
      )
    : label
    ? label
    : intl.formatMessage({ id: 'PriceFilter.label' });

  return (
    <div style={{ marginBottom: '32px' }}>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="h5">{labelText}</Typography>
        <Button color="secondary" onClick={handleClear} sx={{ p: 0 }}>
          <FormattedMessage id="PriceFilter.clear" />
        </Button>
      </Box>

      <PriceFilterForm
        contentRef={(node) => {
          filterContentRef.current = node;
        }}
        id={id}
        initialValues={hasInitialValues ? initialPrice : { minPrice: min, maxPrice: max }}
        intl={intl}
        isOpen={isOpen}
        liveEdit
        max={max}
        min={min}
        onChange={handleChange}
        step={step}
      />
    </div>
  );
};

export default PriceFilterPlain;
